import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getTournaments, createTournament, updateTournament, deleteTournament, submitResult } from './coachApi';

const FORMATS = [
  { id: 'elimination', icon: '🥊', nl: 'Knock-out', en: 'Single Elimination' },
  { id: 'roundrobin',  icon: '🔄', nl: 'Competitie', en: 'Round Robin' },
  { id: 'league',      icon: '📊', nl: 'Competitie + Standen', en: 'League Table' },
];

function generateRoundRobin(participants) {
  const matches = [];
  const p = [...participants];
  if (p.length % 2 !== 0) p.push({ name: 'BYE' });
  const rounds = p.length - 1;
  for (let r = 0; r < rounds; r++) {
    for (let i = 0; i < p.length / 2; i++) {
      const home = p[i];
      const away = p[p.length - 1 - i];
      if (home.name !== 'BYE' && away.name !== 'BYE') {
        matches.push({ id: `${r}-${i}`, round: r + 1, home: home.name, away: away.name, home_score: null, away_score: null });
      }
    }
    p.splice(1, 0, p.pop());
  }
  return matches;
}

function generateBracket(participants) {
  const p = [...participants];
  while ((p.length & (p.length - 1)) !== 0) p.push({ name: 'BYE' });
  const matches = [];
  let mid = 0;
  for (let i = 0; i < p.length; i += 2) {
    matches.push({ id: `r1-${mid}`, round: 1, home: p[i].name, away: p[i + 1].name, home_score: null, away_score: null });
    mid++;
  }
  return matches;
}

function Bracket({ matches, nl, onResult }) {
  const rounds = [...new Set(matches.map(m => m.round))].sort((a, b) => a - b);
  return (
    <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
      <div style={{ display: 'flex', gap: 16, minWidth: rounds.length * 200 }}>
        {rounds.map(r => (
          <div key={r} style={{ flex: 1, minWidth: 180 }}>
            <div style={{ color: _T.accent, fontSize: 10, fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>
              {nl ? `RONDE ${r}` : `ROUND ${r}`}
            </div>
            {matches.filter(m => m.round === r).map(m => (
              <div key={m.id} style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ color: m.winner === m.home ? '#4AC87A' : C.white, fontSize: 12, fontWeight: m.winner === m.home ? 700 : 400 }}>{m.home}</span>
                  <input type="number" value={m.home_score ?? ''} min={0}
                    onChange={e => onResult(m.id, { home_score: Number(e.target.value), away_score: m.away_score })}
                    style={{ width: 32, background: '#080e1a', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 6, color: C.white, fontSize: 13, fontWeight: 700, textAlign: 'center', padding: '3px', outline: 'none' }} />
                </div>
                <div style={{ width: '100%', height: 1, background: _T.cardBorderBot, marginBottom: 6 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: m.winner === m.away ? '#4AC87A' : C.white, fontSize: 12, fontWeight: m.winner === m.away ? 700 : 400 }}>{m.away}</span>
                  <input type="number" value={m.away_score ?? ''} min={0}
                    onChange={e => onResult(m.id, { home_score: m.home_score, away_score: Number(e.target.value) })}
                    style={{ width: 32, background: '#080e1a', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 6, color: C.white, fontSize: 13, fontWeight: 700, textAlign: 'center', padding: '3px', outline: 'none' }} />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Standings({ matches, participants, nl }) {
  const table = participants.map(p => {
    const games = matches.filter(m => (m.home === p.name || m.away === p.name) && m.home_score !== null);
    let w = 0, d = 0, l = 0, gf = 0, ga = 0;
    games.forEach(m => {
      const isHome = m.home === p.name;
      const myScore = isHome ? m.home_score : m.away_score;
      const theirScore = isHome ? m.away_score : m.home_score;
      gf += myScore; ga += theirScore;
      if (myScore > theirScore) w++;
      else if (myScore === theirScore) d++;
      else l++;
    });
    return { name: p.name, p: games.length, w, d, l, gf, ga, gd: gf - ga, pts: w * 3 + d };
  }).sort((a, b) => b.pts - a.pts || b.gd - a.gd);

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ color: C.silver2 }}>
            <th style={{ textAlign: 'left', padding: '6px 4px', fontWeight: 600 }}>#</th>
            <th style={{ textAlign: 'left', padding: '6px 4px', fontWeight: 600 }}>{nl ? 'Team' : 'Team'}</th>
            <th style={{ textAlign: 'center', padding: '6px 4px', fontWeight: 600 }}>P</th>
            <th style={{ textAlign: 'center', padding: '6px 4px', fontWeight: 600 }}>W</th>
            <th style={{ textAlign: 'center', padding: '6px 4px', fontWeight: 600 }}>D</th>
            <th style={{ textAlign: 'center', padding: '6px 4px', fontWeight: 600 }}>L</th>
            <th style={{ textAlign: 'center', padding: '6px 4px', fontWeight: 600 }}>GD</th>
            <th style={{ textAlign: 'center', padding: '6px 4px', fontWeight: 700, color: _T.accent }}>PTS</th>
          </tr>
        </thead>
        <tbody>
          {table.map((row, i) => (
            <tr key={row.name} style={{ borderTop: `1px solid ${_T.cardBorderBot}` }}>
              <td style={{ padding: '8px 4px', color: i < 3 ? _T.accent : C.silver2, fontWeight: i < 3 ? 700 : 400 }}>{i + 1}</td>
              <td style={{ padding: '8px 4px', color: C.white, fontWeight: 600 }}>{row.name}</td>
              <td style={{ padding: '8px 4px', textAlign: 'center', color: C.silver }}>{row.p}</td>
              <td style={{ padding: '8px 4px', textAlign: 'center', color: '#4AC87A' }}>{row.w}</td>
              <td style={{ padding: '8px 4px', textAlign: 'center', color: '#C8A84B' }}>{row.d}</td>
              <td style={{ padding: '8px 4px', textAlign: 'center', color: '#C05050' }}>{row.l}</td>
              <td style={{ padding: '8px 4px', textAlign: 'center', color: row.gd >= 0 ? '#4AC87A' : '#C05050' }}>{row.gd >= 0 ? '+' : ''}{row.gd}</td>
              <td style={{ padding: '8px 4px', textAlign: 'center', color: _T.accent, fontWeight: 700 }}>{row.pts}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CoachTournamentsTab({ coachId, lang }) {
  const nl = lang === 'nl';
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeTournament, setActiveTournament] = useState(null);
  const [showNew, setShowNew]         = useState(false);
  const [form, setForm]               = useState({ name: '', format: 'roundrobin', sport: '⚽', start_date: '', participants: '' });
  const [saving, setSaving]           = useState(false);
  const [activeView, setActiveView]   = useState('bracket');

  const load = useCallback(() => {
    getTournaments(coachId).then(d => { if (Array.isArray(d)) setTournaments(d); setLoading(false); }).catch(() => setLoading(false));
  }, [coachId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.participants.trim()) return;
    setSaving(true);
    const participants = form.participants.split('\n').map(n => ({ name: n.trim() })).filter(p => p.name);
    const matches = form.format === 'elimination' ? generateBracket(participants) : generateRoundRobin(participants);
    await createTournament(coachId, { ...form, participants, matches, status: 'active' }).catch(() => {});
    setSaving(false);
    setShowNew(false);
    setForm({ name: '', format: 'roundrobin', sport: '⚽', start_date: '', participants: '' });
    load();
  };

  const handleResultChange = async (tid, matchId, scores) => {
    const t = tournaments.find(x => x.id === tid) || activeTournament;
    if (!t) return;
    const winner = scores.home_score != null && scores.away_score != null
      ? (scores.home_score > scores.away_score ? t.matches.find(m => m.id === matchId)?.home : scores.home_score < scores.away_score ? t.matches.find(m => m.id === matchId)?.away : null)
      : null;
    const updatedMatches = (t.matches || []).map(m => m.id === matchId ? { ...m, ...scores, winner } : m);
    const updated = { ...t, matches: updatedMatches };
    setActiveTournament(updated);
    setTournaments(ts => ts.map(x => x.id === tid ? updated : x));
    await submitResult(coachId, tid, matchId, { ...scores, winner }).catch(() => {});
  };

  if (activeTournament) {
    const t = activeTournament;
    const formatDef = FORMATS.find(f => f.id === t.format) || FORMATS[0];
    return (
      <div style={{ padding: '0 0 32px' }}>
        <button onClick={() => setActiveTournament(null)} style={{ background: 'none', border: 'none', color: _T.accent, fontSize: 14, cursor: 'pointer', padding: '0 0 16px' }}>
          ← {nl ? 'Terug' : 'Back'}
        </button>
        <div style={{ color: C.white, fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{t.sport} {t.name}</div>
        <div style={{ color: C.silver, fontSize: 12, marginBottom: 16 }}>
          {nl ? formatDef.nl : formatDef.en} · {t.participants?.length || 0} {nl ? 'deelnemers' : 'participants'}
        </div>

        {/* View toggle */}
        {(t.format === 'roundrobin' || t.format === 'league') && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {[['bracket', nl ? 'Uitslagen' : 'Results'], ['standings', nl ? 'Standen' : 'Standings']].map(([v, l]) => (
              <button key={v} onClick={() => setActiveView(v)}
                style={{ flex: 1, padding: '8px', borderRadius: 10, border: `1.5px solid ${activeView === v ? _T.accent : _T.cardBorderBot}`, background: activeView === v ? `${_T.accent}20` : _T.bg4, color: activeView === v ? _T.accent : C.silver, fontSize: 12, fontWeight: activeView === v ? 700 : 400, cursor: 'pointer' }}>
                {l}
              </button>
            ))}
          </div>
        )}

        <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, border: `1px solid ${_T.cardBorderBot}` }}>
          {activeView === 'standings' && (t.format === 'roundrobin' || t.format === 'league')
            ? <Standings matches={t.matches || []} participants={t.participants || []} nl={nl} />
            : <Bracket matches={t.matches || []} nl={nl} onResult={(mid, scores) => handleResultChange(t.id, mid, scores)} />
          }
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ color: C.white, fontSize: 17, fontWeight: 700 }}>{nl ? 'Toernooien' : 'Tournaments'}</div>
        <button onClick={() => setShowNew(v => !v)}
          style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}55`, borderRadius: 10, color: _T.accent, fontSize: 13, fontWeight: 600, padding: '8px 14px', cursor: 'pointer' }}>
          + {nl ? 'Nieuw' : 'New'}
        </button>
      </div>

      {showNew && (
        <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder={nl ? 'Toernooinaam *' : 'Tournament name *'}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }} />

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {FORMATS.map(f => (
              <button key={f.id} onClick={() => setForm(fm => ({ ...fm, format: f.id }))}
                style={{ padding: '6px 12px', borderRadius: 20, border: `1.5px solid ${form.format === f.id ? _T.accent : _T.cardBorderBot}`, background: form.format === f.id ? `${_T.accent}20` : _T.bg4, color: form.format === f.id ? _T.accent : C.silver, fontSize: 11, cursor: 'pointer' }}>
                {f.icon} {nl ? f.nl : f.en}
              </button>
            ))}
          </div>

          <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }} />

          <div style={{ color: C.silver, fontSize: 11, marginBottom: 4 }}>{nl ? 'Deelnemers (één per regel) *' : 'Participants (one per line) *'}</div>
          <textarea value={form.participants} onChange={e => setForm(f => ({ ...f, participants: e.target.value }))}
            placeholder={nl ? 'Team A\nTeam B\nTeam C\n...' : 'Team A\nTeam B\nTeam C\n...'}
            rows={6}
            style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none', marginBottom: 10 }} />

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowNew(false)} style={{ flex: 1, background: 'none', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 13, padding: '10px', cursor: 'pointer' }}>
              {nl ? 'Annuleren' : 'Cancel'}
            </button>
            <button onClick={handleCreate} disabled={saving || !form.name.trim() || !form.participants.trim()}
              style={{ flex: 1, background: form.name.trim() && form.participants.trim() ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: 'none', borderRadius: 10, color: form.name.trim() && form.participants.trim() ? '#0a1220' : C.silver2, fontWeight: 700, fontSize: 13, padding: '10px', cursor: 'pointer' }}>
              {saving ? '...' : (nl ? 'Aanmaken' : 'Create')}
            </button>
          </div>
        </div>
      )}

      {loading ? <div style={{ textAlign: 'center', color: C.silver, padding: 32 }}>...</div> : tournaments.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🏆</div>
          <div>{nl ? 'Nog geen toernooien' : 'No tournaments yet'}</div>
        </div>
      ) : (
        tournaments.map(t => {
          const fmt = FORMATS.find(f => f.id === t.format);
          const done = (t.matches || []).filter(m => m.home_score !== null).length;
          const total = (t.matches || []).length;
          return (
            <div key={t.id} style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 14, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ color: C.white, fontSize: 15, fontWeight: 700 }}>{t.sport} {t.name}</div>
                  <div style={{ color: C.silver, fontSize: 12 }}>{nl ? fmt?.nl : fmt?.en} · {t.participants?.length || 0} {nl ? 'deelnemers' : 'participants'}</div>
                  {total > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.silver2, marginBottom: 3 }}>
                        <span>{done}/{total} {nl ? 'wedstrijden gespeeld' : 'matches played'}</span>
                        <span>{Math.round((done / total) * 100)}%</span>
                      </div>
                      <div style={{ background: _T.bg4, borderRadius: 4, height: 5 }}>
                        <div style={{ width: `${(done / total) * 100}%`, height: '100%', background: `linear-gradient(90deg,${_T.grad1},${_T.grad2})`, borderRadius: 4 }} />
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, marginLeft: 10 }}>
                  <button onClick={() => setActiveTournament(t)}
                    style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}44`, borderRadius: 8, color: _T.accent, fontSize: 12, fontWeight: 600, padding: '6px 12px', cursor: 'pointer' }}>
                    {nl ? 'Openen' : 'Open'}
                  </button>
                  <button onClick={() => { if (window.confirm(nl ? 'Verwijderen?' : 'Delete?')) deleteTournament(coachId, t.id).then(load); }}
                    style={{ background: 'none', border: 'none', color: '#C05050', fontSize: 18, cursor: 'pointer' }}>×</button>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
